// src/contexts/CartContext.tsx
import React, { createContext, ReactNode, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../services/supabase';
import { CartItem } from '../types';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';

interface CartContextType {
    cartItems: CartItem[];
    loading: boolean;
    totalItems: number;
    totalAmount: number;
    addToCart: (productId: number, variantId: number, quantity?: number) => Promise<boolean>;
    updateQuantity: (cartItemId: string, newQuantity: number) => Promise<void>;
    removeFromCart: (cartItemId: string) => Promise<void>;
    clearCart: () => Promise<void>;
    fetchCartItems: () => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [cartItems, setCartItems] = useState<CartItem[]>([]);
    const [initialLoading, setInitialLoading] = useState(false);
    const { user, loading: authLoading } = useAuth();

    const isMounted = useRef(true);
    const isFetching = useRef(false);
    const hasFetchedOnce = useRef(false);

    const totalItems = cartItems.reduce((total, item) => total + item.quantity, 0);
    const totalAmount = cartItems.reduce((total, item) => {
        const price = item.variant?.price || 0;
        return total + price * item.quantity;
    }, 0);

    const fetchCartItems = useCallback(async () => {
        const { data: { session } } = await supabase.auth.getSession();

        if (!session?.user) {
            if (isMounted.current) {
                setCartItems([]);
                setInitialLoading(false);
                hasFetchedOnce.current = true;
            }
            return;
        }

        if (isFetching.current) return;
        isFetching.current = true;

        if (!hasFetchedOnce.current && isMounted.current) {
            setInitialLoading(true);
        }

        try {
            const { data, error } = await supabase
                .from('customer_cart')
                .select(`
                    *,
                    product:products(*),
                    variant:product_variants(*)
                `)
                .eq('customer_id', session.user.id);

            if (error) throw error;

            if (isMounted.current) {
                setCartItems(data || []);
            }
        } catch (error) {
            console.error('[Cart] Error fetching cart:', error);
            if (isMounted.current) {
                toast.error('Ошибка загрузки корзины');
            }
        } finally {
            if (isMounted.current) {
                setInitialLoading(false);
                hasFetchedOnce.current = true;
            }
            isFetching.current = false;
        }
    }, []);

    const addToCart = useCallback(async (productId: number, variantId: number, quantity: number = 1): Promise<boolean> => {
        const { data: { session } } = await supabase.auth.getSession();

        if (!session?.user) {
            toast.error('Войдите в аккаунт для добавления в корзину');
            return false;
        }

        try {
            const { data, error } = await supabase
                .rpc('add_to_cart_atomic', {
                    p_product_id: productId,
                    p_variant_id: variantId,
                    p_quantity: quantity,
                });

            if (error) throw error;

            if (!data?.success) {
                toast.error(data?.message || 'Ошибка добавления');
                return false;
            }

            toast.success(data.message || 'Товар добавлен в корзину');
            await fetchCartItems();
            return true;
        } catch (error) {
            console.error('[Cart] Error adding to cart:', error);
            toast.error('Ошибка добавления в корзину');
            return false;
        }
    }, [fetchCartItems]);

    // Сначала объявляем removeFromCart
    const removeFromCart = useCallback(async (cartItemId: string): Promise<void> => {
        let removedItem: CartItem | undefined;
        setCartItems(prev => {
            removedItem = prev.find(i => i.id === cartItemId);
            return prev.filter(i => i.id !== cartItemId);
        });

        try {
            const { error } = await supabase
                .from('customer_cart')
                .delete()
                .eq('id', cartItemId);

            if (error) throw error;

            toast.success('Товар удален из корзины');
        } catch (error) {
            console.error('[Cart] Error removing item:', error);
            if (removedItem) {
                setCartItems(prev => [...prev, removedItem!]);
            }
            toast.error('Ошибка удаления товара');
        }
    }, []);

    // Потом updateQuantity, который использует removeFromCart
    const updateQuantity = useCallback(async (cartItemId: string, newQuantity: number): Promise<void> => {
        if (newQuantity < 1) {
            await removeFromCart(cartItemId);
            return;
        }

        let originalQuantity = 0;
        setCartItems(prev => {
            const item = prev.find(i => i.id === cartItemId);
            if (item) originalQuantity = item.quantity;
            return prev.map(i => i.id === cartItemId ? { ...i, quantity: newQuantity } : i);
        });

        try {
            const { data, error } = await supabase
                .rpc('update_cart_quantity', {
                    p_cart_id: cartItemId,
                    p_quantity: newQuantity,
                });

            if (error || !data?.success) {
                setCartItems(prev =>
                    prev.map(i => i.id === cartItemId ? { ...i, quantity: originalQuantity } : i)
                );
                toast.error(data?.message || 'Ошибка обновления');
            }
        } catch (error) {
            console.error('[Cart] Error updating quantity:', error);
            setCartItems(prev =>
                prev.map(i => i.id === cartItemId ? { ...i, quantity: originalQuantity } : i)
            );
            toast.error('Ошибка обновления количества');
        }
    }, [removeFromCart]);

    const clearCart = useCallback(async (): Promise<void> => {
        if (!user) return;

        const prevItems = cartItems;
        setCartItems([]);

        try {
            const { error } = await supabase
                .from('customer_cart')
                .delete()
                .eq('customer_id', user.id);

            if (error) throw error;
        } catch (error) {
            console.error('[Cart] Error clearing cart:', error);
            setCartItems(prevItems);
            toast.error('Ошибка очистки корзины');
        }
    }, [user, cartItems]);

    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);

    useEffect(() => {
        if (authLoading) return;
        hasFetchedOnce.current = false;
        fetchCartItems();
    }, [user?.id, authLoading, fetchCartItems]);

    return (
        <CartContext.Provider value={{
            cartItems,
            loading: initialLoading,
            totalItems,
            totalAmount,
            addToCart,
            updateQuantity,
            removeFromCart,
            clearCart,
            fetchCartItems,
        }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = (): CartContextType => {
    const context = useContext(CartContext);
    if (context === undefined) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};